
import { Record } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { class_type, record_type, float64_type, int32_type } from "../../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { sampleValue, Window$, WaveSlice_$reflection, Window$_$reflection } from "./WaveSlice.js";
import { WSConfig_$reflection, NumberBase_$reflection } from "../../Common/CommonTypes.js";
import { GapStore_$reflection } from "../../Model/ModelType.js";
import { filter, add, tryFind, empty } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { equals, compare } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { contains } from "../../fable_modules/fable-library-js.5.13.0/Set.js";
import { FastData, FastBits } from "../../Simulator/SimGraphTypes.js";
import { toUInt32 } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { filter as filter_1, length } from "../../fable_modules/fable-library-js.5.13.0/List.js";

/**
 * Everything a drawn waveform is a function of.
 * 
 * Two waveforms with equal specs are the same picture, so this is what the memo is keyed on. It is
 * not a description of the waveform for anyone to read: it exists to be compared.
 * 
 * `Config` is carried whole rather than picked apart because the drawing reads the font from it,
 * and a field added there that the drawing then uses would otherwise silently stop invalidating
 * the memo. Everything else is named individually because it is not all in one place.
 */
export class WaveSpec extends Record {
    constructor(Driver, Width, Window$, Radix, ColumnWidth, Config) {
        super();
        this.Driver = Driver;
        this.Width = (Width | 0);
        this.Window = Window$;
        this.Radix = Radix;
        this.ColumnWidth = ColumnWidth;
        this.Config = Config;
    }
}

export function WaveSpec_$reflection() {
    return record_type("WaveDrawn.WaveSpec", [], WaveSpec, () => [["Driver", int32_type], ["Width", int32_type], ["Window", Window$_$reflection()], ["Radix", NumberBase_$reflection()], ["ColumnWidth", float64_type], ["Config", WSConfig_$reflection()]]);
}

/**
 * One waveform as it is on screen.
 */
export class Drawn extends Record {
    constructor(Spec, Svg, Gaps, Samples) {
        super();
        this.Spec = Spec;
        this.Svg = Svg;
        this.Gaps = Gaps;
        this.Samples = Samples;
    }
}

export function Drawn_$reflection() {
    return record_type("WaveDrawn.Drawn", [], Drawn, () => [["Spec", WaveSpec_$reflection()], ["Svg", class_type("Fable.React.ReactElement", undefined)], ["Gaps", GapStore_$reflection()], ["Samples", WaveSlice_$reflection()]]);
}

let drawn = empty({
    Compare: (x, y) => (compare(x, y) | 0),
});

/**
 * What the viewer is asking for, for one wave, right now.
 */
export function specOf(ws, wave) {
    return new WaveSpec(wave.DriverIndex, wave.Width, new Window$(ws.StartCycle, ws.SamplingZoom, ws.ShownCycles), ws.Radix, ws.WaveformColumnWidth, ws.WSConfig);
}

/**
 * What is on screen for one wave, if anything ever has been.
 */
export function tryDrawn(driver) {
    return tryFind(driver, drawn);
}

export function put(d) {
    drawn = add(d.Spec.Driver, d, drawn);
}

/**
 * Forget the waveforms of waves no longer selected. Called from the refresh, which is where the
 * selection is settled.
 */
export function keepOnly(drivers) {
    drawn = filter((driver, _arg) => contains(driver, drivers), drawn);
}

/**
 * Forget everything. Called when the simulation is replaced: a driver index names a different
 * signal in the next build, so every entry here is now a picture of something else.
 */
export function forget() {
    drawn = empty({
        Compare: (x, y) => (compare(x, y) | 0),
    });
}

/**
 * The value of one drawn waveform at one of its samples, or None outside it.
 * 
 * Indexed by SAMPLE within the picture rather than by clock cycle, because that is what "under the
 * cursor" means: the cursor is drawn at a column of the waveform, and the value beside it should
 * be the one that column was drawn from. While the picture is a window behind the controls - the
 * data for the view asked for is still on its way - reading it by absolute cycle instead says
 * nothing at all, which is where the value column's row of "?" beside perfectly good waveforms
 * came from.
 */
export function valueAtSample(d, sample) {
    if ((sample < 0) ? true : (sample >= d.Spec.Window.SampleCount)) {
        return undefined;
    }
    else {
        const v = sampleValue(d.Samples, sample);
        return (d.Spec.Width > 32) ? (new FastData(new FastBits(/* BigWord */ 1, [v]), d.Spec.Width)) : (new FastData(new FastBits(/* Word */ 0, [toUInt32(v) >>> 0]), d.Spec.Width));
    }
}

/**
 * How many of these waves are showing a view other than the one the controls ask for.
 * 
 * Zero at rest. Not zero for a moment after the window moves, which is the fallback working; not
 * zero for a long time means data that was asked for is not arriving.
 */
export function staleCount(ws, waves) {
    return length(filter_1((wave) => {
        const matchValue = tryDrawn(wave.DriverIndex);
        if (matchValue == null) {
            return false;
        }
        else {
            return !equals(matchValue.Spec, specOf(ws, wave));
        }
    }, waves)) | 0;
}

